import { Box } from '@mui/material';
import { GitCompare } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { STRUCTURE_SET_NOT_SHOWN_HINT_ID } from '~/hooks/target/structureSetNotShown';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { compareModeActiveState } from '~/state/atoms/structureSet/roiAtoms';

/**
 * Contour comparison toggle for the right-edge operations toolbar (rendered on
 * the Review page only, see {@link OperationsToolbar}). Comparison is a QA /
 * study tool, so it lives here with the other viewport tools rather than in the
 * contouring step toolbar. Toggling drives the floating compare panel and the
 * comparison overlay via {@link compareModeActiveState}; the Review step
 * clears that state on exit.
 *
 * The comparison draws on the Environment that shows the active structure
 * set. While the set sits on no view there is nothing to draw on, so the
 * toggle refuses to open with that reason. A comparison that is on while the
 * set is not shown is not lit, its tooltip says why, and it stays switchable
 * off from here, so the aid can always be cleared by the control that turns
 * it on; the panel stays open and names the state.
 */
export default function ContourCompareSection() {
    const intl = useIntl();
    const [compareModeActive, setCompareModeActive] = useRecoilState(
        compareModeActiveState
    );
    const { environment } = useActiveEditTarget();
    const structureSetShown = environment !== undefined;
    const isDrawn = compareModeActive && structureSetShown;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<GitCompare />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'compare.tool.tooltip' }),
                    !structureSetShown
                        ? intl.formatMessage({
                              id: STRUCTURE_SET_NOT_SHOWN_HINT_ID,
                          })
                        : undefined
                )}
                tooltipPlacement="left"
                selected={isDrawn}
                ariaPressed={isDrawn}
                disabled={!compareModeActive && !structureSetShown}
                onClick={() => setCompareModeActive((active) => !active)}
                data-testid="contour-compare-toggle"
            />
        </Box>
    );
}
